import actionTypes from "../common/actionTypes";
import { DATABASE_STATUTES, SETTINGS_TYPE } from "../common/constants";
import { handleProductEdgesForViewing, handleCollectionEdgesForViewing } from "./helpers/products";

const productReducer = (state, action) => {
  switch (action.type) {
    // fetch pre-order products
    case actionTypes.FETCH_PRE_ORDERED_PRODUCTS:
      return {
        ...state,
        loading: true,
        error: null,
        timesactProducts: {
          keys: new Set(),
          entries: {},
          displayData: {
            po: [],
            bis: [],
            cs: [],
            is: [],
          },
        },
        statuses: {
          ...state.statuses,
          loadPreorder: DATABASE_STATUTES.LOADING,
        },
      };

    case actionTypes.FETCH_PRE_ORDERED_PRODUCTS_SUCCESS: {
      const displayData = Object.values(action.payload.entries).reduce(
        (acc, product) => {
          const variants = Object.entries(product.variants).map(([id, variant]) => ({ id, ...variant }));

          if (
            variants.some((variant) => {
              return "isPOEnabled" in variant ? variant["isPOEnabled"] : true;
            })
          ) {
            acc.po.push({
              ...product,
              variants: variants
                .filter((variant) => ("isPOEnabled" in variant ? variant["isPOEnabled"] : true))
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            });
          }

          if (variants.some((variant) => variant["isBISEnabled"])) {
            acc.bis.push({
              ...product,
              variants: variants
                .filter((variant) => variant["isBISEnabled"])
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            });
          }

          if (variants.some((variant) => variant["isCSEnabled"])) {
            acc.cs.push({
              ...product,
              variants: variants
                .filter((variant) => variant["isCSEnabled"])
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            });
          }

          if (variants.some((variant) => variant["isISEnabled"])) {
            acc.is.push({
              ...product,
              variants: variants
                .filter((variant) => variant["isISEnabled"])
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            });
          }

          return acc;
        },
        {
          po: [],
          bis: [],
          cs: [],
          is: [],
        }
      );

      return {
        ...state,
        timesactProducts: {
          keys: action.payload.keys,
          entries: action.payload.entries,
          displayData,
        },
        statuses: {
          ...state.statuses,
          loadPreorder: DATABASE_STATUTES.LOADED,
        },
        loading: false,
      };
    }

    case actionTypes.FETCH_PRE_ORDERED_PRODUCTS_FAILURE:
      return {
        ...state,
        error: action.payload,
        statuses: {
          ...state.statuses,
          loadPreorder: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    // Process pre-orders
    case actionTypes.PROCESS_PRE_ORDERED_PRODUCTS:
      return {
        ...state,
        statuses: {
          ...state.statuses,
          savePreorder: DATABASE_STATUTES.IN_PROGRESS,
        },
        filtered: {
          data: [],
          hasNextPage: false,
          count: 0,
          entries: {},
        },
        progress: 0,
      };

    case actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_UPDATE:
      return {
        ...state,
        statuses: {
          ...state.statuses,
          savePreorder: DATABASE_STATUTES.IN_PROGRESS,
        },
        progress: action.payload,
      };

    case actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_SUCCESS:
      return {
        ...state,
        statuses: {
          ...state.statuses,
          savePreorder: DATABASE_STATUTES.LOADED,
        },
        progress: 0,
        savedPreOrderIds: [],
        productPageConfig: {
          ...state.productPageConfig,
          prevPage: 0,
        },
      };

    case actionTypes.PROCESS_PRE_ORDERED_PRODUCTS_FAILURE:
      return {
        ...state,
        error: action.payload,
        savedPreOrderIds: [],
      };

    // tab / table page
    case actionTypes.SAVE_TAB_TO_STORE:
      return {
        ...state,
        productPageConfig: {
          ...state.productPageConfig,
          tab: action.payload,
        },
        statuses: {
          ...state.statuses,
          savePreorder: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    // Fetch products
    case actionTypes.FETCH_PRODUCTS:
      return {
        ...state,
        productPageConfig: {
          ...state.productPageConfig,
          prevPage: action.payload,
        },
        statuses: {
          ...state.statuses,
          loadProducts: action.isFetchingMoreProducts ? DATABASE_STATUTES.LOADED : DATABASE_STATUTES.LOADING,
        },
        error: null,
      };

    case actionTypes.FETCH_PRODUCTS_SUCCESS:
      return {
        ...state,
        products: {
          data: [...new Map([...state.products.data, ...action.payload.data].map((item) => [item.id, item])).values()],
          count: action.payload.count,
          cursor: action.payload.cursor,
          pageInfo: action.payload.pageInfo,
        },
        statuses: {
          ...state.statuses,
          loadProducts: DATABASE_STATUTES.LOADED,
        },
      };

    case actionTypes.FETCH_PRODUCTS_FAILURE:
      return {
        ...state,
        error: action.payload,
        statuses: {
          ...state.statuses,
          loadProducts: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    // Fetch Collection
    case actionTypes.FETCH_COLLECTIONS:
      return {
        ...state,
        statuses: {
          ...state.statuses,
          loadCollections: action.isFetchingMoreCollections ? DATABASE_STATUTES.LOADED : DATABASE_STATUTES.LOADING,
        },
        error: null,
      };

    case actionTypes.FETCH_COLLECTIONS_SUCCESS:
      return {
        ...state,
        collections: {
          data: [
            ...new Map([...state.collections.data, ...action.payload.data].map((item) => [item.id, item])).values(),
          ],

          count: action.payload.count,
          cursor: action.payload.cursor,
          pageInfo: action.payload.pageInfo,
        },
        statuses: {
          ...state.statuses,
          loadCollections: DATABASE_STATUTES.LOADED,
        },
      };

    case actionTypes.FETCH_COLLECTIONS_FAILURE:
      return {
        ...state,
        error: action.payload,
        statuses: {
          ...state.statuses,
          loadCollections: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    // find product
    case actionTypes.FIND_PRODUCT:
      return {
        ...state,
        filtered: action.isFetchingMoreProducts
          ? state.filtered
          : {
              data: [],
              hasNextPage: false,
              count: 0,
              entries: {},
            },
        statuses: {
          ...state.statuses,
          loadProducts: action.isFetchingMoreProducts ? DATABASE_STATUTES.LOADED : DATABASE_STATUTES.LOADING,
        },
      };

    case actionTypes.FIND_PRODUCT_SUCCESS:
      return {
        ...state,
        filtered: {
          data: handleProductEdgesForViewing(state.filtered.data, action.payload.edges),
          hasNextPage: action.payload.pageInfo.hasNextPage || false,
          count: state.filtered.count + action.payload.edges.length,
          entries: {},
        },
        statuses: {
          ...state.statuses,
          loadProducts: DATABASE_STATUTES.LOADED,
        },
      };

    case actionTypes.FIND_PRODUCT_FAILURE:
      return {
        ...state,
        error: action.payload,
        statuses: {
          ...state.statuses,
          loadProducts: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    // Find collection.
    case actionTypes.FIND_COLLECTION:
      return {
        ...state,
        filtered: {
          data: [],
          hasNextPage: false,
          count: 0,
          entries: {},
        },
        statuses: {
          ...state.statuses,
          loadCollections: DATABASE_STATUTES.LOADING,
        },
      };

    case actionTypes.FIND_COLLECTION_SUCCESS:
      return {
        ...state,
        filtered: {
          data: handleCollectionEdgesForViewing(action.payload.edges),
          hasNextPage: false,
          count: action.payload.edges.length,
          entries: {},
        },
        statuses: {
          ...state.statuses,
          loadCollections: DATABASE_STATUTES.LOADED,
        },
      };

    case actionTypes.FIND_COLLECTION_FAILURE:
      return {
        ...state,
        error: action.payload,
        statuses: {
          ...state.statuses,
          loadCollections: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    // search
    case actionTypes.CLEAR_SEARCH:
      return {
        ...state,
        filtered: {
          data: [],
          hasNextPage: false,
          count: 0,
          entries: {},
        },
      };

    // search
    case actionTypes.SAVE_PRODUCT_IDS:
      return {
        ...state,
        selectedItems: action.payload.ids,
        selectedItemsType: action.payload.type,
      };

    // filters
    case actionTypes.SAVE_PRODUCT_FILTERS:
      return {
        ...state,
        filters: action.payload,
      };

    // additional products - hasMultipleProducts = true
    case actionTypes.FETCH_ADDITIONAL_TIMESACT_PRODUCTS:
      return {
        ...state,
        filtered: {
          data: [],
          entries: {},
          hasNextPage: false,
          count: 0,
        },
        statuses: {
          ...state.statuses,
          loadPreorder: DATABASE_STATUTES.LOADING,
        },
        loading: true,
      };

    case actionTypes.FETCH_ADDITIONAL_TIMESACT_PRODUCTS_SUCCESS: {
      const { data, type, entries } = action.payload;

      const products = data
        .map((product) => {
          const variants = Object.entries(product.variants).map(([id, variant]) => ({ id, ...variant }));

          if (
            type === SETTINGS_TYPE.PO &&
            variants.some((variant) => {
              return "isPOEnabled" in variant ? variant["isPOEnabled"] : true;
            })
          ) {
            return {
              ...product,
              variants: variants
                .filter((variant) => ("isPOEnabled" in variant ? variant["isPOEnabled"] : true))
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            };
          }

          if (type === SETTINGS_TYPE.BIS && variants.some((variant) => variant["isBISEnabled"])) {
            return {
              ...product,
              variants: variants
                .filter((variant) => variant["isBISEnabled"])
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            };
          }

          if (type === SETTINGS_TYPE.CS && variants.some((variant) => variant["isCSEnabled"])) {
            return {
              ...product,
              variants: variants
                .filter((variant) => variant["isCSEnabled"])
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            };
          }

          if (type === SETTINGS_TYPE.IS && variants.some((variant) => variant["isISEnabled"])) {
            return {
              ...product,
              variants: variants
                .filter((variant) => variant["isISEnabled"])
                .sort(({ id: a }, { id: b }) => (a > b ? 1 : -1)),
            };
          }

          return null;
        })
        .filter((product) => product);

      return {
        ...state,
        filtered: {
          data: products,
          entries,
          hasNextPage: false,
          count: action.payload.data.length,
        },
        statuses: {
          ...state.statuses,
          loadPreorder: DATABASE_STATUTES.LOADED,
        },
        loading: false,
      };
    }

    case actionTypes.FETCH_ADDITIONAL_TIMESACT_PRODUCTS_FAILURE:
      return {
        ...state,
        error: action.payload,
        statuses: {
          ...state.statuses,
          loadPreorder: DATABASE_STATUTES.LOADED_ERROR,
        },
      };

    case actionTypes.REMOVE_VARIANTS_IN_FILTERED_PRODUCTS:
      return {
        ...state,
        filtered: {
          ...state.filtered,
          data: state.filtered.data.map((product) => {
            const processedProductVariants = action.payload[product.id];

            if (processedProductVariants) {
              return {
                ...product,
                variants: product.variants.filter((variant) => !processedProductVariants.has(variant.id)),
              };
            }

            return product;
          }),
        },
      };

    case actionTypes.REMOVE_ALL_FILTERED_PRODUCTS:
      return {
        ...state,
        filtered: {
          entries: {},
          data: [],
          count: 0,
          hasNextPage: false,
        },
      };

    case actionTypes.UPDATE_FILTERED_PRODUCTS: {
      const { productId, variantId, variantProcessedData } = action.payload;

      return {
        ...state,
        filtered: {
          ...state.filtered,
          data: state.filtered.data.map((product) => {
            if (product.id === productId) {
              return {
                ...product,
                variants: product.variants.map((variant) => {
                  if (variant.id === variantId) {
                    return {
                      ...variant,
                      ...variantProcessedData,
                    };
                  }

                  return variant;
                }),
              };
            }

            return product;
          }),
          entries: {
            ...state.filtered.entries,
            ...(state.filtered.entries[productId] && {
              [productId]: {
                ...state.filtered.entries[productId],
                variants: {
                  ...state.filtered.entries[productId].variants,
                  [variantId]: {
                    ...state.filtered.entries[productId].variants[variantId],
                    ...variantProcessedData,
                  },
                },
              },
            }),
          },
        },
      };
    }

    default:
      return state;
  }
};

export default productReducer;
